import { APP_STORAGE_NAMESPACE } from '../utils/storageNames'

/**
 * Clean up is a developer feature (docs/screenshot-cleanup/PLAN.md): it shows only for the current server-verified administrator who
 * also switched it on for this account on this device. The switch never grants anything without the verified administrator.
 */
const key = (owner: string) => `${APP_STORAGE_NAMESPACE}:dev-screenshot-cleanup:v1:${owner}`

export function readCleanupPreference(owner: string | null): boolean {
  if (!owner) return false
  try { return localStorage.getItem(key(owner)) === 'on' } catch { return false }
}

export function writeCleanupPreference(owner: string, enabled: boolean): void {
  if (enabled) localStorage.setItem(key(owner), 'on')
  else localStorage.removeItem(key(owner))
}

export const cleanupAvailable = (developerAuthorized: boolean, requested: boolean) => developerAuthorized && requested

/**
 * What a developer feature already holds on this device (a note's unpacked cards, covers and cleaned view; Contexts) is local data and shows with no connection. The
 * administrator check is a network call, so offline it cannot answer; the last answer, kept on this device, stands in for display only.
 * Running Unpack (a model read, a sync) still needs the live check (`cleanupAvailable`). An online "not an administrator" always wins,
 * so withdrawing the feature from an account still takes the cards away.
 */
const verdictKey = (owner: string) => `${APP_STORAGE_NAMESPACE}:dev-admin-verdict:v1:${owner}`

export function readRememberedAdmin(owner: string | null): boolean {
  if (!owner) return false
  try { return localStorage.getItem(verdictKey(owner)) === 'admin' } catch { return false }
}

export function rememberAdmin(owner: string, isAdmin: boolean): void {
  try {
    if (isAdmin) localStorage.setItem(verdictKey(owner), 'admin')
    else localStorage.removeItem(verdictKey(owner))
  } catch { /* the check still gates running Unpack; only offline display is lost */ }
}

export const developerFeatureShown = (requested: boolean, authorized: boolean, deniedOnline: boolean, remembered: boolean) =>
  requested && (authorized || (!deniedOnline && remembered))

/** What Unpack does along the way (Settings → Unpack), for this account on this device; everything is on until switched off. */
export interface UnpackPreferences {
  /** The cleaned photo is the note's picture in Browse. */
  cover: boolean
  /** The rest of a post is added below lines pasted from it. */
  addRest: boolean
  /** A dated booking shows in Quick Access from the day before to the day after. */
  quickAccess: boolean
  /** A booking offers a reminder the day before (set only on a tap). */
  remind: boolean
}
export const DEFAULT_UNPACK_PREFERENCES: UnpackPreferences = { cover: true, addRest: true, quickAccess: true, remind: true }
const preferencesKey = (owner: string) => `${APP_STORAGE_NAMESPACE}:unpack-preferences:v1:${owner}`

export function readUnpackPreferences(owner: string | null): UnpackPreferences {
  if (!owner) return DEFAULT_UNPACK_PREFERENCES
  try {
    const stored = JSON.parse(localStorage.getItem(preferencesKey(owner)) ?? '{}') as Partial<Record<keyof UnpackPreferences, unknown>>
    return Object.fromEntries(Object.entries(DEFAULT_UNPACK_PREFERENCES).map(([name, value]) => [name, typeof stored[name as keyof UnpackPreferences] === 'boolean' ? stored[name as keyof UnpackPreferences] : value])) as unknown as UnpackPreferences
  } catch { return DEFAULT_UNPACK_PREFERENCES }
}

export function writeUnpackPreferences(owner: string, preferences: UnpackPreferences): void {
  localStorage.setItem(preferencesKey(owner), JSON.stringify(preferences))
}
